import { useNotificationStore, Notification } from '../stores/notificationStore'
import { useNotificationSettingsStore } from '../stores/notificationSettingsStore'
import i18next from 'i18next'

// Interface for notification options to create a notification
interface CreateNotificationOptions {
  title: string
  message: string
  type: 'info' | 'success' | 'warning' | 'error'
  data?: Record<string, any>
}

// Helper function to ensure translation returns a string
const translate = (key: string, data?: Record<string, any>): string => {
  return i18next.t(key, data).toString()
}

// Transaction Notifications
export const createTransactionNotification = async ({
  type,
  amount,
  currency,
  category,
  account,
  data
}: {
  type: 'created' | 'deleted' | 'edited' | 'large' | 'recurring' | 'categoryChanged' | 'transferComplete'
  amount?: number
  currency?: string
  category?: string
  account?: string
  data?: Record<string, any>
}) => {
  // Check notification settings first
  const settings = useNotificationSettingsStore.getState().settings
  
  // Map notification types to settings keys
  const settingsMap = {
    created: settings.transactionCreated,
    deleted: settings.transactionDeleted,
    edited: settings.transactionCreated, // Use same setting as created
    large: settings.largeTransactions,
    recurring: settings.recurringTransactions,
    categoryChanged: settings.categoryChanged,
    transferComplete: settings.transferComplete
  }

  // Check if this notification type is enabled
  if (!settingsMap[type]) {
    console.log(`Transaction notification type '${type}' is disabled in settings`)
    return null
  }

  // Check if push notifications are enabled for transaction notifications
  if (!settings.pushNotifications) {
    console.log('Push notifications are disabled in settings')
    return null
  }

  const { addNotification } = useNotificationStore.getState()

  // Ensure we have a default value for currency
  const formattedCurrency = currency || 'TL'; // Default to TL if not provided
  
  // Format the combined data to ensure all required fields are present
  const formattedData = {
    amount: amount,
    currency: formattedCurrency, // Always include currency in data
    category: category || '',
    account: account || '',
    ...data
  };
  
  console.log('Creating transaction notification:', {
    type,
    formattedData
  });
  
  // Map notification types to their corresponding translation keys
  const notifications = {
    created: {
      title: 'notifications.transactions.created.title',
      message: 'notifications.transactions.created.message'
    },
    deleted: {
      title: 'notifications.transactions.deleted.title',
      message: 'notifications.transactions.deleted.message'
    },
    edited: {
      title: 'notifications.transactions.edited.title',
      message: 'notifications.transactions.edited.message'
    },
    large: {
      title: 'notifications.transactions.large.title',
      message: 'notifications.transactions.large.message'
    },
    recurring: {
      title: 'notifications.transactions.recurring.title',
      message: 'notifications.transactions.recurring.message'
    },
    categoryChanged: {
      title: 'notifications.transactions.categoryChanged.title',
      message: 'notifications.transactions.categoryChanged.message'
    },
    transferComplete: {
      title: 'notifications.transactions.transferComplete.title',
      message: 'notifications.transactions.transferComplete.message'
    }
  };

  // Get the notification titles/messages for this type
  const notification = notifications[type] || {
    title: 'notifications.transactions.default.title',
    message: 'notifications.transactions.default.message'
  };

  return addNotification({
    title: notification.title,
    message: notification.message,
    type: 'info',
    data: formattedData
  });
}

// System Notifications
export const createSystemNotification = async ({
  type,
  data
}: {
  type: 'exchangeRate' | 'newFeature' | 'update' | 'security' | 'sync'
  data?: Record<string, any>
}) => {
  // Check notification settings first
  const settings = useNotificationSettingsStore.getState().settings
  
  // Map notification types to settings keys
  const settingsMap = {
    exchangeRate: settings.exchangeRateUpdates,
    newFeature: settings.newFeatures,
    update: settings.appUpdates,
    security: settings.securityAlerts,
    sync: settings.syncStatus
  }

  // Check if this notification type is enabled
  if (!settingsMap[type]) {
    console.log(`System notification type '${type}' is disabled in settings`)
    return null
  }

  // Check if push notifications are enabled for system notifications
  if (!settings.pushNotifications) {
    console.log('Push notifications are disabled in settings')
    return null
  }

  const { addNotification } = useNotificationStore.getState()

  // Map to translation keys directly
  const notifications: Record<string, CreateNotificationOptions> = {
    exchangeRate: {
      title: 'notifications.system.exchangeRate.title',
      message: 'notifications.system.exchangeRate.message',
      type: 'info',
    },
    newFeature: {
      title: 'notifications.system.newFeature.title',
      message: 'notifications.system.newFeature.message',
      type: 'info',
    },
    update: {
      title: 'notifications.system.update.title',
      message: 'notifications.system.update.message',
      type: 'info',
    },
    security: {
      title: 'notifications.system.security.title',
      message: 'notifications.system.security.message',
      type: 'warning',
    },
    sync: {
      title: 'notifications.system.sync.title',
      message: 'notifications.system.sync.message',
      type: 'info',
    },
  }

  // Use the addNotification method from the store
  const notification = notifications[type]
  return await addNotification({
    ...notification,
    data
  })
}

// Budget Notifications
type BudgetNotificationType = 
  | 'approaching'  // When spending is approaching the budget limit
  | 'exceeded'     // When spending has exceeded the budget
  | 'reset'        // When a budget period resets
  | 'created'      // When a new budget is created
  | 'updated'      // When a budget is updated
  | 'deleted'      // When a budget is deleted

interface BudgetNotificationData extends Record<string, any> {
  budgetName: string
  category?: string
  currentSpending?: number
  budgetLimit: number
  currency: string
  percentage?: number
  periodType?: 'monthly' | 'yearly'
}

export const createBudgetNotification = async ({
  type,
  data
}: {
  type: BudgetNotificationType
  data: BudgetNotificationData
}) => {
  // Check notification settings first
  const settings = useNotificationSettingsStore.getState().settings
  
  // Map notification types to settings keys
  const settingsMap = {
    approaching: settings.budgetApproaching,
    exceeded: settings.budgetExceeded,
    reset: settings.budgetReset,
    created: settings.budgetApproaching, // Use approaching setting for created
    updated: settings.budgetApproaching, // Use approaching setting for updated
    deleted: settings.budgetApproaching  // Use approaching setting for deleted
  }

  // Check if this notification type is enabled
  if (!settingsMap[type]) {
    console.log(`Budget notification type '${type}' is disabled in settings`)
    return null
  }

  // Check if push notifications are enabled for budget notifications
  if (!settings.pushNotifications) {
    console.log('Push notifications are disabled in settings')
    return null
  }

  const { addNotification } = useNotificationStore.getState()

  // Map to translation keys directly instead of translating here
  const notifications: Record<string, CreateNotificationOptions> = {
    approaching: {
      title: 'notifications.budgets.approaching.title',
      message: 'notifications.budgets.approaching.message',
      type: 'warning',
    },
    exceeded: {
      title: 'notifications.budgets.exceeded.title',
      message: 'notifications.budgets.exceeded.message',
      type: 'error',
    },
    reset: {
      title: 'notifications.budgets.reset.title',
      message: 'notifications.budgets.reset.message',
      type: 'info',
    },
    created: {
      title: 'notifications.budgets.created.title',
      message: 'notifications.budgets.created.message',
      type: 'info',
    },
    updated: {
      title: 'notifications.budgets.updated.title',
      message: 'notifications.budgets.updated.message',
      type: 'info',
    },
    deleted: {
      title: 'notifications.budgets.deleted.title',
      message: 'notifications.budgets.deleted.message',
      type: 'info',
    },
  }

  // Format numeric values to strings for translation interpolation
  const formattedData = {
    ...data,
    budgetName: data.budgetName,
    category: data.category,
    currentSpending: data.currentSpending !== undefined ? String(data.currentSpending) : undefined,
    budgetLimit: String(data.budgetLimit),
    currency: data.currency,
    percentage: data.percentage !== undefined ? String(data.percentage) : undefined,
    periodType: data.periodType
  }

  // Use the addNotification method from the store
  const notification = notifications[type]
  return await addNotification({
    ...notification,
    data: formattedData
  })
}

// Account Notifications
export const createAccountNotification = async (options: {
  type: 'lowBalance' | 'negativeBalance' | 'largeChange' | 'syncStatus'
  account: string
  balance?: number
  currency?: string
  data?: Record<string, any>
}) => {
  // Check notification settings first
  const settings = useNotificationSettingsStore.getState().settings
  
  // Map notification types to settings keys (using existing settings)
  const settingsMap = {
    lowBalance: settings.largeTransactions, // Use large transactions setting
    negativeBalance: settings.largeTransactions, // Use large transactions setting
    largeChange: settings.largeTransactions,
    syncStatus: settings.syncStatus
  }

  // Check if this notification type is enabled
  if (!settingsMap[options.type]) {
    console.log(`Account notification type '${options.type}' is disabled in settings`)
    return null
  }

  // Check if push notifications are enabled
  if (!settings.pushNotifications) {
    console.log('Push notifications are disabled in settings')
    return null
  }

  const { addNotification } = useNotificationStore.getState()
  const { type, account, balance, currency, data } = options

  // Map types to notification messages
  const notifications: Record<string, CreateNotificationOptions> = {
    lowBalance: {
      title: 'notifications.accounts.lowBalance.title',
      message: 'notifications.accounts.lowBalance.message',
      type: 'warning',
    },
    negativeBalance: {
      title: 'notifications.accounts.negativeBalance.title',
      message: 'notifications.accounts.negativeBalance.message',
      type: 'error',
    },
    largeChange: {
      title: 'notifications.accounts.largeChange.title',
      message: 'notifications.accounts.largeChange.message',
      type: 'info',
    },
    syncStatus: {
      title: 'notifications.accounts.syncStatus.title',
      message: 'notifications.accounts.syncStatus.message',
      type: 'info',
    },
  }

  // Format numeric values to strings for translation interpolation
  const formattedData = {
    ...data,
    account,
    balance: balance !== undefined ? String(balance) : undefined,
    currency
  }

  // Use the addNotification method from the store
  const notification = notifications[type]
  return await addNotification({
    ...notification,
    data: formattedData
  })
}

// Goal Notifications
export const createGoalNotification = async (options: {
  type: 'milestone' | 'deadline' | 'targetMissed' | 'completed'
  goal: string
  percentage?: number
  daysLeft?: number
  amount?: number
  currency?: string
  data?: Record<string, any>
}) => {
  // Check notification settings first
  const settings = useNotificationSettingsStore.getState().settings
  
  // For now, use system notifications setting for goals
  // In the future, we could add separate goal notification settings
  if (!settings.newFeatures || !settings.pushNotifications) {
    console.log('Goal notifications are disabled in settings')
    return null
  }

  const { addNotification } = useNotificationStore.getState()
  const { type, goal, percentage, daysLeft, amount, currency, data } = options

  // Map types to notification messages
  const notifications: Record<string, CreateNotificationOptions> = {
    milestone: {
      title: 'notifications.goals.milestone.title',
      message: 'notifications.goals.milestone.message',
      type: 'success',
    },
    deadline: {
      title: 'notifications.goals.deadline.title',
      message: 'notifications.goals.deadline.message',
      type: 'warning',
    },
    targetMissed: {
      title: 'notifications.goals.targetMissed.title',
      message: 'notifications.goals.targetMissed.message',
      type: 'error',
    },
    completed: {
      title: 'notifications.goals.completed.title',
      message: 'notifications.goals.completed.message',
      type: 'success',
    },
  }

  // Format numeric values to strings for translation interpolation
  const formattedData = {
    ...data,
    goal,
    percentage: percentage !== undefined ? String(percentage) : undefined,
    daysLeft: daysLeft !== undefined ? String(daysLeft) : undefined,
    amount: amount !== undefined ? String(amount) : undefined,
    currency
  }

  // Use the addNotification method from the store
  const notification = notifications[type]
  return await addNotification({
    ...notification,
    data: formattedData
  })
}

export default {
  createTransactionNotification,
  createBudgetNotification,
  createAccountNotification,
  createGoalNotification,
  createSystemNotification
}
